import type { Components, Theme } from '@mui/material/styles';

import { motion, tokens } from './tokens';

/** Premium offset focus ring on every interactive element. */
const focusVisible = (theme: Theme) => ({
  '&:focus-visible': {
    outline: 'none',
    ...theme.applyStyles('light', { boxShadow: tokens.focusRing.light }),
    ...theme.applyStyles('dark', { boxShadow: tokens.focusRing.dark }),
  },
});

const tableHeadSurface = (theme: Theme) => ({
  ...theme.applyStyles('light', {
    backgroundColor: tokens.color.surface.panel,
  }),
  ...theme.applyStyles('dark', {
    backgroundColor: tokens.color.surface.darkElevated,
  }),
});

/** Micro-interaction timing shared by interactive elements (motion-map R2). */
const microTransition = `background-color ${String(motion.durationMs.micro)}ms ease-out, box-shadow ${String(motion.durationMs.micro)}ms ease-out, border-color ${String(motion.durationMs.micro)}ms ease-out, color ${String(motion.durationMs.micro)}ms ease-out, transform ${String(motion.durationMs.micro)}ms ease-out`;

export const components: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: 'var(--mui-palette-background-default)',
        color: 'var(--mui-palette-text-primary)',
      },
      '::selection': {
        backgroundColor: 'rgba(45, 106, 106, 0.22)',
      },
      /** Precision scrollbars — slim, rounded, quiet. */
      '*': {
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(15, 38, 38, 0.18) transparent',
      },
      '*::-webkit-scrollbar': { width: 8, height: 8 },
      '*::-webkit-scrollbar-track': { background: 'transparent' },
      '*::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(15, 38, 38, 0.18)',
        borderRadius: 999,
        '&:hover': { backgroundColor: 'rgba(15, 38, 38, 0.30)' },
      },
      '[data-mui-color-scheme="dark"] *::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(255, 255, 255, 0.16)',
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.28)' },
      },
      /** Fallback ring for non-MUI focusables (e.g. the canvas). */
      ':focus-visible': {
        outline: '2px solid rgba(45, 106, 106, 0.6)',
        outlineOffset: 2,
      },
      /** Motion rule R4: reduced motion disables every animation, app-wide. */
      '@media (prefers-reduced-motion: reduce)': {
        '*, *::before, *::after': {
          animationDuration: '0.01ms !important',
          animationIterationCount: '1 !important',
          transitionDuration: '0.01ms !important',
        },
      },
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: tokens.radius.md,
        paddingInline: tokens.spacing * 2.5,
        textTransform: 'none',
        fontWeight: 600,
        transition: microTransition,
        '&:active': { transform: 'scale(0.98)' },
        ...focusVisible(theme),
        // Anodized key, not a flat fill. `background` shorthand on purpose:
        // hover must re-declare the gradient or MUI's backgroundColor wins.
        ...theme.applyStyles('light', {
          '&.MuiButton-containedPrimary': {
            background: tokens.gradient.primary,
            boxShadow:
              'inset 0 1px 0 rgba(255,255,255,0.20), 0 1px 2px rgba(15,38,38,0.20), 0 4px 12px -2px rgba(45,106,106,0.35)',
            '&:hover': {
              background: tokens.gradient.primaryHover,
              boxShadow:
                'inset 0 1px 0 rgba(255,255,255,0.25), 0 2px 4px rgba(15,38,38,0.20), 0 6px 16px -2px rgba(45,106,106,0.40)',
            },
            '&:active': {
              background: tokens.gradient.primaryActive,
              boxShadow: 'inset 0 2px 4px rgba(13,34,34,0.25)',
            },
          },
          // Secondary buttons read as physical keys.
          '&.MuiButton-outlined': {
            backgroundColor: tokens.color.neutral[0],
            borderColor: tokens.color.surface.hairlineStrong,
            boxShadow: tokens.shadow.sm,
            '&:hover': {
              borderColor: tokens.color.brand[300],
              backgroundColor: tokens.color.neutral[0],
              boxShadow: tokens.shadow.md,
            },
          },
        }),
        ...theme.applyStyles('dark', {
          '&.MuiButton-containedPrimary': {
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25)',
          },
        }),
      }),
      sizeLarge: { paddingBlock: 10, paddingInline: tokens.spacing * 3.5, fontSize: '0.9375rem' },
    },
  },
  MuiTextField: { defaultProps: { variant: 'outlined', size: 'small', fullWidth: true } },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: tokens.radius.md,
        color: 'var(--mui-palette-text-primary)',
        transition: microTransition,
        // Machined recess instead of a flat outline.
        ...theme.applyStyles('light', {
          backgroundColor: tokens.color.neutral[0],
          boxShadow: 'inset 0 1px 2px rgba(15,38,38,0.05)',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.color.brand[300],
          },
          '&.Mui-focused': {
            boxShadow: 'inset 0 1px 2px rgba(15,38,38,0.04), 0 0 0 3px rgba(45,106,106,0.14)',
          },
        }),
        ...theme.applyStyles('dark', {
          backgroundColor: tokens.color.surface.darkElevated,
          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.3)',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.color.brand[400],
          },
          '&.Mui-focused': {
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.25), 0 0 0 3px rgba(126,186,186,0.18)',
          },
        }),
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--mui-palette-primary-main)',
          borderWidth: 2,
        },
      }),
      notchedOutline: ({ theme }) => ({
        transition: microTransition,
        ...theme.applyStyles('light', {
          borderColor: tokens.color.surface.hairlineStrong,
        }),
        ...theme.applyStyles('dark', {
          borderColor: tokens.color.surface.darkBorder,
        }),
      }),
      input: {
        '&::placeholder': {
          color: 'var(--mui-palette-text-secondary)',
          opacity: 1,
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        borderRadius: tokens.radius.lg,
        backgroundColor: 'var(--mui-palette-background-paper)',
        color: 'var(--mui-palette-text-primary)',
      },
      outlined: ({ theme }) => ({
        ...theme.applyStyles('light', { borderColor: tokens.color.surface.hairline }),
        ...theme.applyStyles('dark', { borderColor: tokens.color.surface.darkHairline }),
      }),
    },
  },
  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: tokens.radius.lg,
        border: '1px solid',
        backgroundColor: 'var(--mui-palette-background-paper)',
        color: 'var(--mui-palette-text-primary)',
        transition: microTransition,
        ...theme.applyStyles('light', {
          borderColor: tokens.color.surface.hairline,
          boxShadow: tokens.shadow.card,
        }),
        ...theme.applyStyles('dark', {
          borderColor: tokens.color.surface.darkHairline,
          boxShadow: tokens.shadow.darkCard,
        }),
      }),
    },
  },
  MuiCardActionArea: {
    styleOverrides: {
      root: { borderRadius: 'inherit' },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: tokens.radius.xxl,
        ...theme.applyStyles('light', { boxShadow: tokens.shadow.overlay }),
        ...theme.applyStyles('dark', { boxShadow: tokens.shadow.darkOverlay }),
      }),
    },
  },
  MuiBackdrop: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:not(.MuiBackdrop-invisible)': {
          ...theme.applyStyles('light', { backgroundColor: tokens.color.surface.scrim }),
          ...theme.applyStyles('dark', { backgroundColor: tokens.color.surface.darkScrim }),
        },
      }),
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: tokens.radius.md,
        marginTop: 4,
        ...theme.applyStyles('light', { boxShadow: tokens.shadow.float }),
        ...theme.applyStyles('dark', { boxShadow: tokens.shadow.darkFloat }),
      }),
      list: { padding: 6 },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.sm,
        marginInline: 0,
        paddingBlock: 8,
        transition: microTransition,
      },
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: ({ theme }) => ({
        ...theme.applyStyles('light', { boxShadow: tokens.shadow.float }),
        ...theme.applyStyles('dark', { boxShadow: tokens.shadow.darkFloat }),
      }),
    },
  },
  MuiAppBar: {
    defaultProps: { color: 'default', elevation: 0 },
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        backgroundColor: 'var(--mui-palette-background-paper)',
        borderBottom: '1px solid var(--mui-palette-divider)',
        color: 'var(--mui-palette-text-primary)',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: '1px solid var(--mui-palette-divider)',
        backgroundColor: 'var(--mui-palette-background-paper)',
        color: 'var(--mui-palette-text-primary)',
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: tokens.radius.pill,
        color: 'var(--mui-palette-text-primary)',
        transition: microTransition,
        ...focusVisible(theme),
        '& .MuiListItemIcon-root': {
          color: 'var(--mui-palette-text-secondary)',
        },
        '&:hover': {
          backgroundColor: 'var(--mui-palette-action-hover)',
        },
        '&.Mui-selected, &.Mui-selected:hover': {
          background: tokens.gradient.navPill,
          color: '#ffffff',
          fontWeight: 600,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.22), 0 2px 6px rgba(45,106,106,0.30)',
          '& .MuiListItemIcon-root': { color: 'inherit' },
        },
      }),
    },
  },
  MuiListItemText: {
    styleOverrides: {
      primary: {
        color: 'inherit',
      },
      secondary: {
        color: 'var(--mui-palette-text-secondary)',
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        height: 3,
        borderRadius: 3,
        boxShadow: '0 1px 2px rgba(45, 106, 106, 0.4)',
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        textTransform: 'none',
        fontWeight: 600,
        transition: microTransition,
        ...focusVisible(theme),
      }),
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-root': {
          fontWeight: 600,
          borderBottomColor: 'var(--mui-palette-divider)',
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        color: 'var(--mui-palette-text-primary)',
        borderBottomColor: 'var(--mui-palette-divider)',
        fontVariantNumeric: 'tabular-nums',
      },
      head: ({ theme }) => ({
        color: 'var(--mui-palette-text-primary)',
        ...tableHeadSurface(theme),
      }),
      stickyHeader: ({ theme }) => ({
        color: 'var(--mui-palette-text-primary)',
        ...tableHeadSurface(theme),
      }),
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        transition: microTransition,
        '&:hover': {
          backgroundColor: 'var(--mui-palette-action-hover)',
        },
      },
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      root: {
        color: 'var(--mui-palette-text-primary)',
        borderTop: '1px solid var(--mui-palette-divider)',
      },
      toolbar: {
        color: 'var(--mui-palette-text-primary)',
      },
      selectLabel: {
        color: 'var(--mui-palette-text-secondary)',
      },
      displayedRows: {
        color: 'var(--mui-palette-text-secondary)',
      },
      select: {
        color: 'var(--mui-palette-text-primary)',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: tokens.radius.pill,
        fontWeight: 600,
        transition: microTransition,
        ...theme.applyStyles('light', {
          '&.MuiChip-filledDefault': {
            backgroundColor: tokens.color.neutral[50],
            border: '1px solid rgba(15,38,38,0.08)',
          },
        }),
      }),
      sizeSmall: { fontSize: '0.75rem' },
    },
  },
  MuiTooltip: {
    defaultProps: { arrow: true },
    styleOverrides: {
      tooltip: ({ theme }) => ({
        borderRadius: tokens.radius.sm,
        fontSize: '0.75rem',
        paddingInline: 10,
        paddingBlock: 6,
        boxShadow: '0 4px 12px rgba(13, 34, 34, 0.3)',
        ...theme.applyStyles('light', {
          backgroundColor: tokens.color.brand[900],
        }),
      }),
      arrow: ({ theme }) => ({
        ...theme.applyStyles('light', {
          color: tokens.color.brand[900],
        }),
      }),
    },
  },
  MuiSwitch: {
    styleOverrides: {
      track: ({ theme }) => ({
        borderRadius: 999,
        transition: microTransition,
        ...theme.applyStyles('light', {
          backgroundColor: tokens.color.neutral[200],
          boxShadow: tokens.shadow.insetWell,
          opacity: 1,
        }),
      }),
      thumb: {
        backgroundColor: '#ffffff',
        boxShadow:
          '0 0 0 1px rgba(15,38,38,0.10), 0 1px 2px rgba(15,38,38,0.25), inset 0 1px 0 rgba(255,255,255,0.9)',
      },
      switchBase: ({ theme }) => ({
        ...theme.applyStyles('light', {
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: tokens.color.brand[500],
            opacity: 1,
          },
        }),
      }),
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: tokens.radius.pill,
        height: 8,
        ...theme.applyStyles('light', {
          backgroundColor: tokens.color.surface.well,
          boxShadow: tokens.shadow.insetWell,
        }),
        ...theme.applyStyles('dark', {
          backgroundColor: tokens.color.surface.darkElevated,
          boxShadow: tokens.shadow.darkInsetWell,
        }),
      }),
      bar: ({ theme }) => ({
        borderRadius: tokens.radius.pill,
        ...theme.applyStyles('light', {
          background: tokens.gradient.navPill,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25)',
        }),
      }),
    },
  },
  MuiSkeleton: {
    defaultProps: { animation: 'wave' },
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.applyStyles('dark', {
          backgroundColor: tokens.color.surface.darkElevated,
        }),
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: 'var(--mui-palette-text-primary)',
        transition: microTransition,
        ...focusVisible(theme),
      }),
    },
  },
};
